import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Publication } from 'app/interfaces/publication';
import { Subscription } from 'rxjs';
import { PublicationService } from 'app/services/publication.service';
import { Component, Input, Output, ViewChild, HostListener, EventEmitter, ElementRef, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { BbSelectOption } from '../bb-select/bb-select.component';

import { BbDatepickerComponent } from '..//bb-datepicker/bb-datepicker.component';
import { TranslateService } from '@ngx-translate/core';
import { DateRange } from '../../../interfaces/dateRange';
import { DateRange as MatDateRange, MatCalendar } from '@angular/material/datepicker';
import { DatePresetsSelectorService } from '../../../services/date-presets-selector.service';

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

@Component({
	selector: 'bb-date-presets',
	templateUrl: './bb-date-presets.component.html',
	styleUrls: ['./bb-date-presets.component.scss']
})

export class BbDatePresetsComponent implements OnInit, OnChanges, OnDestroy {
	@Input() minDate: Date = null;
	@Input() minDateWarning: string = null;
	@Input() icon: string = null;
	@Input() time = false;
	@Input() hidePresets = false;
	@Input() useMatFormField = false;
	private _preset: string;
	@Input() set preset(val) {
		this._preset = val;
		this.presetLabel = this.presets.find(option => option.value === val)?.label;
	};
	get preset() {
		return this._preset;
	}
	@Output() presetChange = new EventEmitter<string>();
	@Input() startDate: Date = null;
	@Input() endDate: Date = null;
	@Input() theme = 'default';
	@Input() showInline = false;
	@Input() usePublicationTimeZone = false;
	@Input() isLoading: boolean;
	@Input() presets: BbSelectOption[] = [
		{ value: 'custom', label: this.translate.instant('CUSTOM') },
		{ value: 'today', label: this.translate.instant('TODAY') },
		{ value: 'yesterday', label: this.translate.instant('YESTERDAY') },
		{ value: 'last-7-days', label: this.translate.instant('LAST_X_DAYS', { x: '7' }) },
		{ value: 'last-30-days', label: this.translate.instant('LAST_X_DAYS', { x: '30' }) },
		{ value: 'last-week', label: this.translate.instant('LAST_WEEK') },
		{ value: 'last-month', label: this.translate.instant('LAST_MONTH') },
		{ value: 'last-quarter', label: this.translate.instant('LAST_QUARTER') },
		{ value: 'last-6-months', label: this.translate.instant('LAST_6_MONTHS') },
		{ value: 'last-year', label: this.translate.instant('LAST_YEAR') },
		{ value: 'week-to-date', label: this.translate.instant('WEEK_TO_DATE') },
		{ value: 'month-to-date', label: this.translate.instant('MONTH_TO_DATE') },
		{ value: 'quarter-to-date', label: this.translate.instant('QUARTER_TO_DATE') },
		{ value: 'year-to-date', label: this.translate.instant('YEAR_TO_DATE') }
	];

	@Output() onChanged = new EventEmitter<DateRange>();

	@ViewChild('startDatePicker') startDatePicker: BbDatepickerComponent;
	@ViewChild('endDatePicker') endDatePicker: BbDatepickerComponent;
	@ViewChild('rangeCal1', { static: false }) rangeCal1: MatCalendar<Date>;
	@ViewChild('rangeCal2', { static: false }) rangeCal2: MatCalendar<Date>;
	@ViewChild('matEndDateInput', { static: false }) matEndDateInput: ElementRef<HTMLInputElement>;
	@ViewChild('matStartDateInput', { static: false }) matStartDateInput: ElementRef<HTMLInputElement>;
	@ViewChild('scrollPresetContainer', { static: false }) scrollContainerDiv: ElementRef<HTMLDivElement>;

	presetLabel = '';
	dontAlterNext = false;
	initialStartDate: Date = null;
	initialEndDate: Date = null;
	expanded = false;
	datesCorrected = false;
	determiningPreset = false;
	today = new Date();
	pubTimeZoneName: string;
	localTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
	subscriptions = new Subscription();
	timeZoneMismatch = false;
	_isOpen = false;
	changeFromForm = false;
	set isOpen(val) {
		this._isOpen = val;
		setTimeout(() => {
			this.updateActiveMonths();
			this.scrollPresetInView();
		});
	}
	get isOpen() {
		return this._isOpen;
	}
	// dateRange works as a shadowed value and will update when startDate / endDate change
	dateRange = new UntypedFormGroup({
		start: new UntypedFormControl(),
		end: new UntypedFormControl(),
	});
	selectedRangeValue: MatDateRange<Date> | undefined;

	constructor(
		private translate: TranslateService,
		private datePresetsSelectorService: DatePresetsSelectorService,
		private element: ElementRef,
		private publicationService: PublicationService
	) {
		this.subscriptions.add(this.publicationService.getData().subscribe((pub: Publication) => {
			if (pub && pub.timeZone) {
				this.pubTimeZoneName = pub.timeZone;
				this.timeZoneMismatch = dayjs().utcOffset() !== dayjs().tz(this.pubTimeZoneName).utcOffset();
			}
		}));
	}

	@HostListener('document:keydown', ['$event'])
	onKeydownHandler(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			this.hidePanel();
		}
	}

	ngOnInit() {
		this.disableInvalidPresets();

		if (this.preset === undefined) {
			this.preset = this.datePresetsSelectorService.preset;
			if (this.preset === 'custom') {
				this.startDate = this.datePresetsSelectorService.statsDate.startDate;
				this.endDate = this.datePresetsSelectorService.statsDate.endDate;
			}
			this.determineDatesFromPreset();
		} else if (this.preset) {
			if (this.preset === 'custom' && (!this.startDate && this.datePresetsSelectorService.statsDate?.startDate) && (!this.endDate && this.datePresetsSelectorService.statsDate?.endDate)) {
				this.startDate = this.datePresetsSelectorService.statsDate?.startDate;
				this.endDate = this.datePresetsSelectorService.statsDate?.endDate;
			}
			this.determineDatesFromPreset();
		}

		this.subscriptions.add(this.onChanged.subscribe(() => {
			this.updateMatControl();
		}));
		this.updateMatControl();
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		// minDate changed?
		if (changes.minDate && this.preset) {
			const currentVal = new Date(changes.minDate.currentValue);
			const previousVal = new Date(changes.minDate.previousValue);

			if (!changes.minDate.previousValue || currentVal.getTime() !== previousVal.getTime()) {
				this.determineDatesFromPreset();
			}
		}
	}

	disableInvalidPresets() {
		this.presets.map(preset => {
			switch (preset.value) {
				case 'week-to-date':
					preset.disabled = new Date().getDay() === 1; // not on monday
					break;
				case 'month-to-date':
					preset.disabled = new Date().getDate() === 1; // not on first of month.. etc.
					break;
				case 'quarter-to-date':
					preset.disabled = [0, 3, 6, 9].includes(new Date().getMonth()) && new Date().getDate() === 1;
					break;
				case 'year-to-date':
					preset.disabled = new Date().getMonth() === 0 && new Date().getDate() === 1;
					break;
				default:
					preset.disabled = false;
					break;
			};

			return preset;
		});
	}

	onDateInputChange() {
		if (!this.dateRange.controls.start?.value) {
			this.dateRange.controls.start.setErrors({
				invalid: true
			});
		}
		if (!this.dateRange.controls.end?.value) {
			this.dateRange.controls.end.setErrors({
				invalid: true
			});
		}
		if (this.dateRange.controls.start?.value && this.dateRange.controls.end?.value) {
			this.changeFromForm = true;
			const startDate = this.startDate = this.dateRange.controls.start.value;
			const endDate = this.endDate = this.dateRange.controls.end.value;
			if (this.preset !== 'custom') {
				this.preset = 'custom';
				this.presetChange.emit(this.preset);
			}

			this.onChanged.emit({ startDate, endDate } as DateRange);
			this.changeFromForm = false;
		}
	}

	onDateInputFocus(ev) {
		if (!this.isOpen) {
			const target = ev?.target;
			this.isOpen = true;
			setTimeout(() => {
				if (document.activeElement !== target) {
					target?.focus?.();
				}
			});
		}
	}

	onPresetFieldClick(ev) {
		if (!this.isOpen) {
			this.isOpen = true;
		}
		const closestInput = ev?.target?.nodeName !== 'INPUT' ? ev?.target?.querySelector('input') : ev?.target;
		setTimeout(() => {
			closestInput?.focus();
		});
	}

	onDateInputEnter(ev) {
		const target = ev?.target;
		setTimeout(() => {
			if (document.activeElement !== target) {
				target?.focus?.();
			}
		});
	}

	onStartDateInputTab(ev: KeyboardEvent) {
		setTimeout(() => {
			this.matEndDateInput?.nativeElement?.focus();
		});
	}

	updateMatControl() {
		const selectedRangeValue = {
			start: this.startDate,
			end: this.endDate
		};
		if (!this.changeFromForm) {
			this.dateRange.controls.start.patchValue(this.startDate);
			this.dateRange.controls.end.patchValue(this.endDate);
		}
		this.selectedRangeValue = new MatDateRange<Date>(selectedRangeValue.start, selectedRangeValue.end);
		this.updateActiveMonths();
	}

	updateActiveMonths() {
		const startDate = new Date(this.selectedRangeValue.start);
		const endDate = new Date(this.selectedRangeValue.end);
		if (startDate && endDate && startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
			// if the full range falls in one month, show the previous month in the first calendar
			startDate.setMonth(startDate.getMonth() - 1);
			this.rangeCal1?._goToDateInView(startDate, 'month');
		} else {
			this.rangeCal1?._goToDateInView(this.selectedRangeValue.start, 'month');
		}
		this.rangeCal2?._goToDateInView(this.selectedRangeValue.end, 'month');
	}

	scrollPresetInView(): void {
		if (!this.preset || !this._isOpen || !this.scrollContainerDiv) {
			return;
		}

		const presetIndex = this.presets.findIndex(p => p.value === this.preset);

		if (presetIndex > -1) {
			const child = this.scrollContainerDiv.nativeElement.children[presetIndex];
			if (child) {
				child.scrollIntoView({ block: 'end' });
			}
		}
	}

	selectedChange(m: Date) {
		if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
			this.selectedRangeValue = new MatDateRange<Date>(m, null);
		} else {
			const start = this.selectedRangeValue.start;
			const end = m;
			if (end < start) {
				this.selectedRangeValue = new MatDateRange<Date>(end, start);
			} else {
				this.selectedRangeValue = new MatDateRange<Date>(start, end);
			}
		}
		const startDate = this.startDate = this.selectedRangeValue.start;
		const endDate = this.endDate = this.selectedRangeValue.end;

		if (startDate && endDate) {
			this.preset = 'custom';
			this.determineDatesFromPreset(false);
		}
	}

	startDatepickerClicked(event: MouseEvent) {
		event.stopPropagation();
		this.endDatePicker.close();
	}


	endDatepickerClicked(event: MouseEvent) {
		event.stopPropagation();
		this.startDatePicker.close();
	}

	hidePanel() {
		if (this.expanded) {
			this.togglePanel();
		}
	}

	showPanel() {
		if (!this.expanded) {
			this.togglePanel();
		}
	}

	togglePanel() {
		this.expanded = !this.expanded;
		this.element.nativeElement.className = (this.expanded ? 'expanded' : 'collapsed');
	}


	determineDatesFromPreset(timeChanged = false) {
		if (this.preset === 'custom') {
			if (typeof this.startDate === 'string') {
				this.startDate = new Date(this.startDate);
			}
			if (typeof this.endDate === 'string') {
				this.endDate = new Date(this.endDate);
			}
		}

		this.presetChange.emit(this.preset);
		this.determiningPreset = true;

		let startDate: Dayjs; let endDate: Dayjs;

		if (this.preset && this.preset !== 'custom') {
			this.datePresetsSelectorService.preset = this.preset;
			this.datePresetsSelectorService.statsDate = null;
		}

		if (this.preset === 'custom') {
			if (!timeChanged) {
				const startDayJs = dayjs(this.startDate);
				const endDayJs = dayjs(this.endDate);

				// sorry, timezone horror
				if (this.dontAlterNext) {
					startDate = dayjs(this.startDate).tz(this.pubTimeZoneName).startOf('day');
					endDate = dayjs(this.endDate).tz(this.pubTimeZoneName).endOf('day');
					this.dontAlterNext = false;
				} else {
					startDate = dayjs(this.startDate).tz(this.pubTimeZoneName).startOf('day')
						.set('date', startDayJs.get('date')).set('month', startDayJs.get('month')).set('year', startDayJs.get('year'));
					endDate = dayjs(this.endDate).tz(this.pubTimeZoneName).endOf('day')
						.set('date', endDayJs.get('date')).set('month', endDayJs.get('month')).set('year', endDayJs.get('year'));
				}
				if (dayjs(this.endDate).get('hours') !== 0) {
					this.dontAlterNext = true;
				}
			} else {
				startDate = dayjs(this.startDate).tz(this.pubTimeZoneName);
				endDate = dayjs(this.endDate).tz(this.pubTimeZoneName);
			}
			this.datePresetsSelectorService.preset = this.preset;
			if (startDate && endDate) {
				this.datePresetsSelectorService.statsDate = { startDate: startDate.toDate(), endDate: endDate.toDate() };
			}
		} else {
			// for presets we set the correct DATE and then set the time to the correct hours/minutes later
			startDate = dayjs().tz(this.pubTimeZoneName);
			endDate = dayjs().tz(this.pubTimeZoneName).subtract(1, 'day');
			const now = dayjs().tz(this.pubTimeZoneName);

			switch (this.preset) {

				case 'today':
					endDate = now;
					break;

				case 'yesterday':
					startDate = startDate.subtract(1, 'day');
					break;

				case 'last-24-hours':
					endDate = now;
					startDate = startDate.subtract(1, 'day');
					break;

				case 'last-48-hours':
					endDate = now;
					startDate = startDate.subtract(2, 'day');
					break;

				case 'last-72-hours':
					endDate = now;
					startDate = startDate.subtract(3, 'day');
					break;

				case 'last-7-days':
					startDate = startDate.subtract(7, 'day');
					break;

				case 'last-30-days':
					startDate = startDate.subtract(30, 'day');
					break;

				case 'last-week':

					endDate = endDate.set('date', endDate.get('date') - endDate.get('day'));
					startDate = startDate.set('date', startDate.get('date') - startDate.get('day') - 6);
					break;

				case 'last-month':
					endDate = now;
					startDate = startDate.set('date', 1)
						.set('month', endDate.get('month') - 1);
					endDate = endDate.date(0);
					break;

				case 'last-quarter':
					const month = Math.floor((startDate.get('month') / 3) - 1) * 3;
					startDate = startDate.set('month', month)
						.date(1);

					endDate = endDate.month(month + 3)
						.date(0);
					break;

				case 'last-6-months':
					endDate = now;
					startDate = startDate.subtract(6, 'month').date(1);
					endDate = endDate.date(0);
					break;

				case 'last-year':
					startDate = startDate.subtract(1, 'year')
						.month(0)
						.date(1);

					endDate = endDate.year(startDate.year())
						.month(11)
						.date(31);
					break;

				case 'week-to-date':
					startDate = startDate.date(startDate.date() - startDate.day() + 1);
					break;

				case 'month-to-date':
					startDate = startDate.date(1);
					break;

				case 'quarter-to-date':
					// month 0-2 => 0, 3-5 => 3, 6-8 => 6, 9-11 => 9
					const currentQuartermonth = Math.floor(startDate.get('month') / 3) * 3;
					startDate = startDate.month(currentQuartermonth)
						.set('date', 1);

					break;

				case 'year-to-date':
					startDate = startDate.date(1)
						.month(0);
					break;
			}

			if (this.preset.includes('hours')) {
				const hour = startDate.hour();
				endDate = endDate.hour(hour)
					.minute(0)
					.second(0)
					.millisecond(0);
				startDate = startDate.set('hours', hour)
					.minute(0)
					.second(0)
					.millisecond(0);
			} else if (!timeChanged) {
				// set the time to start and end of day
				startDate = startDate.startOf('day');
				if (this.preset !== 'today') {
					endDate = endDate.endOf('day');
				}
			}
		}

		// console.warn('startDate', startDate.utc().format('YYYY-MM-DDTHH:mm:ss.SSSz'), 'endDate', endDate.utc().format('YYYY-MM-DDTHH:mm:ss.SSSz'));
		// console.warn('=============DIFFFF', startDate.diff(endDate, 'days', true).toFixed(3), 'days, OR', startDate.diff(endDate, 'hours', true).toFixed(3), 'hours');

		// dayJS -> JS date
		let startDateObject = startDate.toDate();
		let endDateObject = endDate.toDate();

		let datesCorrected = false;

		if (this.minDate && startDateObject.getTime() < this.minDate.getTime()) {
			startDateObject = new Date(this.minDate);
			datesCorrected = true;
		}

		if (this.minDate && endDateObject.getTime() < this.minDate.getTime()) {
			endDateObject = new Date(this.minDate);
			datesCorrected = true;
		}

		if (this.preset === 'custom' || !this.startDate || !this.endDate ||
			this.startDate.getTime() !== startDateObject.getTime() || this.endDate.getTime() !== endDateObject.getTime()) {

			this.startDate = startDateObject;
			this.endDate = endDateObject;
			this.datesCorrected = datesCorrected;

			this.initialStartDate = startDateObject;
			this.initialEndDate = endDateObject;

			this.onChanged.emit({ startDate: startDateObject, endDate: endDateObject } as DateRange);
		}

		this.isOpen = false;

		setTimeout(() => {
			this.determiningPreset = false;
		});
		this.updateMatControl();
	}

	checkAndChangeTimeZoneInverted(date: Date) {
		// by setting the inverted timezone we get the correct hour offset for that timezone
		if (!this.usePublicationTimeZone || !this.pubTimeZoneName) {
			return date;
		}
		const invdate = new Date(date.toLocaleString('en-US', {
			timeZone: this.pubTimeZoneName
		}));

		let diff = invdate.getTime() - date.getTime(); // reversed from original function
		if (diff < 1000) {
			diff = 0; // ignore milliseconds
		}
		this.timeZoneMismatch = Math.abs(diff) > 0;
		return new Date(date.getTime() - diff);
	}

	presetChanged() {
		this.determineDatesFromPreset();
	}

	setPreset(preset) {
		this.preset = preset;
		this.determineDatesFromPreset();
	}

	dateChanged(timeChanged = false, activateEndDateInput = false) {
		if (!this.determiningPreset) {
			this.preset = 'custom';
			this.determineDatesFromPreset(timeChanged);
		}

		if (activateEndDateInput) {
			this.startDatePicker.close();
			setTimeout(() => {
				this.endDatePicker.open();
			});
		}
	}
}
